import React, { useState } from 'react';
import { CreditCard, Check, AlertCircle, Smartphone, Banknote } from 'lucide-react';
import { FormButton, FormField } from '../common/Form';
import LoadingSpinner from '../common/LoadingSpinner';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TripFormData } from './TripWizardModal';
import { useTrips } from '../../hooks/useTrips';
import { CreateTripData } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

interface Step4Props {
  data: TripFormData;
  onChange: (data: Partial<TripFormData>) => void;
  onPrev: () => void;
  onSuccess: (tripId: number) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

interface PaymentFormData {
  payment_method: string;
  cardholder_name: string;
  card_number: string;
  expiry_date: string;
  cvv: string;
}

const paymentSchema = yup.object({
  payment_method: yup
    .string()
    .required('Selecione um método de pagamento'),
  cardholder_name: yup
    .string()
    .default(''),
  card_number: yup
    .string()
    .default(''),
  expiry_date: yup
    .string()
    .default(''),
  cvv: yup
    .string()
    .default(''),
});

export function Step4Payment({ data, onChange, onPrev, onSuccess, isLoading, setIsLoading }: Step4Props) {
  const [step, setStep] = useState<'payment' | 'processing' | 'success'>('payment');
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { addTrip, confirmPayment } = useTrips();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<PaymentFormData>({
    resolver: yupResolver(paymentSchema),
    defaultValues: {
      payment_method: data.payment_method || 'credit_card',
    }
  });

  const selectedPaymentMethod = watch('payment_method');

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatCardNumber = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiryDate = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length >= 2) {
      return numbers.substring(0, 2) + '/' + numbers.substring(2, 4);
    }
    return numbers;
  };

  const onSubmit = async (formData: PaymentFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      setStep('processing');

      // Note: For now, we'll just send the participants as provided
      // The logged user should already be included via Step2Participants
      const tripData: CreateTripData = {
        hotel_name: data.hotel_name,
        start_date: data.start_date,
        end_date: data.end_date,
        participants: data.participants.map(p => ({
          name: p.name,
          email: p.email || null,
          document_number: p.document_number,
          date_of_birth: p.date_of_birth,
          phone: p.phone || null,
          country_code: p.country_code || '+55',
          exemption_reason_id: p.exemption_reason_id || null,
        }))
      };

      console.log('🚀 Creating trip with data:', tripData);

      // Create trip
      const response = await addTrip(tripData);
      const tripId = response.id;

      console.log('✅ Trip created successfully:', response);

      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate payment confirmation
      const paymentData = {
        gateway_transaction_id: `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        payment_method: formData.payment_method,
      };

      console.log('💳 Confirming payment:', paymentData);

      await confirmPayment(tripId, paymentData);

      console.log('✅ Payment confirmed successfully');

      setStep('success');
      
      // Wait 5 seconds before calling success (or until user closes modal)
      setTimeout(() => {
        onSuccess(tripId);
      }, 5000);

    } catch (err) {
      console.error('❌ Payment error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao processar pagamento';
      setError(errorMessage);
      setStep('payment');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'processing') {
    return (
      <div className="text-center py-12">
        <LoadingSpinner size="lg" customText="Processando seu pagamento..." />
        <div className="mt-8 max-w-md mx-auto">
          <div className="bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '70%' }}></div>
          </div>
          <p className="text-sm text-gray-500 mt-2">Não feche esta janela</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Pagamento Aprovado! 🎉</h3>
        <p className="text-lg text-gray-700 mb-6">
          Tudo pronto para curtir sua viagem!
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-sm text-green-800 mb-4">
            Sua viagem foi confirmada com sucesso. Os QR codes foram gerados automaticamente e você pode acessá-los pelo dashboard.
          </p>
          <p className="text-xs text-green-600">
            Esta janela fechará automaticamente em alguns segundos...
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <CreditCard className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Pagamento</h3>
        <p className="text-gray-600 mt-2">
          Total a pagar: <span className="font-semibold text-blue-600">{formatCurrency(data.total_amount || 0)}</span>
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Método de Pagamento *
        </label>
        
        <div className="grid grid-cols-1 gap-3">
          {/* Credit Card */}
          <label className={`
            relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
            ${selectedPaymentMethod === 'credit_card' 
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}>
            <input
              type="radio"
              value="credit_card"
              {...register('payment_method')}
              className="sr-only"
              disabled={isLoading}
            />
            <div className="flex items-center space-x-4 flex-1">
              <div className={`
                w-12 h-12 rounded-lg flex items-center justify-center
                ${selectedPaymentMethod === 'credit_card' ? 'bg-blue-100' : 'bg-gray-100'}
              `}>
                <CreditCard className={`h-6 w-6 ${selectedPaymentMethod === 'credit_card' ? 'text-blue-600' : 'text-gray-600'}`} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">Cartão de Crédito</div>
                <div className="text-sm text-gray-500">Visa, Mastercard, Elo</div>
              </div>
            </div>
            <div className={`
              w-5 h-5 rounded-full border-2 flex items-center justify-center
              ${selectedPaymentMethod === 'credit_card' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
            `}>
              {selectedPaymentMethod === 'credit_card' && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
          </label>

          {/* Debit Card */}
          <label className={`
            relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
            ${selectedPaymentMethod === 'debit_card' 
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}>
            <input
              type="radio"
              value="debit_card"
              {...register('payment_method')}
              className="sr-only"
              disabled={isLoading}
            />
            <div className="flex items-center space-x-4 flex-1">
              <div className={`
                w-12 h-12 rounded-lg flex items-center justify-center
                ${selectedPaymentMethod === 'debit_card' ? 'bg-blue-100' : 'bg-gray-100'}
              `}>
                <Banknote className={`h-6 w-6 ${selectedPaymentMethod === 'debit_card' ? 'text-blue-600' : 'text-gray-600'}`} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">Cartão de Débito</div>
                <div className="text-sm text-gray-500">Débito à vista</div>
              </div>
            </div>
            <div className={`
              w-5 h-5 rounded-full border-2 flex items-center justify-center
              ${selectedPaymentMethod === 'debit_card' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
            `}>
              {selectedPaymentMethod === 'debit_card' && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
          </label>

          {/* PIX */}
          <label className={`
            relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
            ${selectedPaymentMethod === 'pix' 
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}>
            <input
              type="radio"
              value="pix"
              {...register('payment_method')}
              className="sr-only"
              disabled={isLoading}
            />
            <div className="flex items-center space-x-4 flex-1">
              <div className={`
                w-12 h-12 rounded-lg flex items-center justify-center
                ${selectedPaymentMethod === 'pix' ? 'bg-blue-100' : 'bg-gray-100'}
              `}>
                <Smartphone className={`h-6 w-6 ${selectedPaymentMethod === 'pix' ? 'text-blue-600' : 'text-gray-600'}`} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">PIX</div>
                <div className="text-sm text-gray-500">Pagamento instantâneo</div>
              </div>
            </div>
            <div className={`
              w-5 h-5 rounded-full border-2 flex items-center justify-center
              ${selectedPaymentMethod === 'pix' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
            `}>
              {selectedPaymentMethod === 'pix' && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
          </label>
        </div>
        
        {errors.payment_method && (
          <p className="text-sm text-red-600">{errors.payment_method.message}</p>
        )}
      </div>

      {/* Credit/Debit Card Form */}
      {(selectedPaymentMethod === 'credit_card' || selectedPaymentMethod === 'debit_card') && (
        <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
          <h4 className="font-medium text-gray-900">
            Dados do {selectedPaymentMethod === 'credit_card' ? 'Cartão de Crédito' : 'Cartão de Débito'}
          </h4>

          <FormField
            label="Nome do Portador"
            name="cardholder_name"
            type="text"
            placeholder="Nome como está no cartão"
            register={register}
            error={errors.cardholder_name}
            required
            disabled={isLoading}
            autoComplete="cc-name"
          />

          <FormField
            label="Número do Cartão"
            name="card_number"
            type="text"
            placeholder="1234 5678 9012 3456"
            register={register}
            error={errors.card_number}
            required
            disabled={isLoading}
            autoComplete="cc-number"
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Data de Validade"
              name="expiry_date"
              type="text"
              placeholder="MM/AA"
              register={register}
              error={errors.expiry_date}
              required
              disabled={isLoading}
              autoComplete="cc-exp"
            />

            <FormField
              label="CVV"
              name="cvv"
              type="text"
              placeholder="123"
              register={register}
              error={errors.cvv}
              required
              disabled={isLoading}
              autoComplete="cc-csc"
            />
          </div>
        </div>
      )}

      {/* PIX Information */}
      {selectedPaymentMethod === 'pix' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Pagamento via PIX</h4>
          <p className="text-sm text-blue-800">
            Após confirmar, você receberá o código PIX para realizar o pagamento através do seu aplicativo bancário.
          </p>
        </div>
      )}

      {/* Security Notice */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 1L5 6v6a4 4 0 108 0V6l-5-5zM8 8a2 2 0 114 0v4a2 2 0 11-4 0V8z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h5 className="font-medium text-gray-900">🔒 Pagamento Seguro</h5>
            <p className="text-sm text-gray-600 mt-1">
              Seus dados são protegidos por criptografia SSL. Não armazenamos informações do seu cartão.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-6">
        <FormButton
          type="button"
          variant="ghost"
          onClick={onPrev}
          disabled={isLoading}
        >
          Voltar
        </FormButton>
        
        <FormButton
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
          className="min-w-[140px]"
        >
          {selectedPaymentMethod === 'pix' ? 'Gerar PIX' : 'Confirmar Pagamento'}
        </FormButton>
      </div>
    </form>
  );
}

export default Step4Payment;
